import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import {
  ArrowRight, FileText, CheckCircle2, AlertCircle,
  Upload, Image as ImageIcon, Shield, Lock, EyeOff,
  Leaf, ScanLine, Search, Handshake, Sparkles,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { httpRequest, HttpError } from '@/lib/http';
import PostScanSummary from '@/components/PostScanSummary';
import EconomyPossibleDialog, { economyCongratsEligible } from '@/components/EconomyPossibleDialog';
import PlanChoiceBeforeAuthDialog from '@/components/PlanChoiceBeforeAuthDialog';

function uploadErrorMessage(err) {
  if (err instanceof HttpError && err.status === 404) {
    return "API introuvable (404). Lancez FastAPI (uvicorn sur le port 8000) ou ajustez REACT_APP_BACKEND_URL dans frontend/.env.development.local si le backend est sur un autre port.";
  }
  if (err instanceof HttpError) return err.message;
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg || JSON.stringify(d)).join(' — ');
  if (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || err.message === 'Failed to fetch' || err.name === 'TypeError') {
    if (process.env.NODE_ENV === 'development') {
      return "Impossible de joindre le serveur. Démarrez l'API : cd backend && uvicorn server:app --reload --host 0.0.0.0 --port 8000";
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base || base === 'undefined') return "Configuration manquante : definissez REACT_APP_BACKEND_URL lors du build production.";
    return "Impossible de joindre le serveur. Verifiez REACT_APP_BACKEND_URL et que l'API est joignable.";
  }
  return err.message || "Erreur lors de l'upload";
}

const SEPARATOR = <div className="max-w-5xl mx-auto px-6 md:px-12 hidden md:block"><div className="border-t border-white/[0.06]" /></div>;
const SEPARATOR_ALWAYS = <div className="max-w-5xl mx-auto px-6 md:px-12"><div className="border-t border-white/[0.06]" /></div>;

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisToken, setAnalysisToken] = useState(null);
  const [error, setError] = useState('');
  const [economyDialogOpen, setEconomyDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [planAuthDialogOpen, setPlanAuthDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const resultsSectionRef = useRef(null);
  const uploadSectionRef = useRef(null);

  useEffect(() => {
    if (analysisResult && !analysisResult.error) {
      resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [analysisResult]);

  useEffect(() => {
    if (!analysisResult) { setEconomyDialogOpen(false); return; }
    if (economyCongratsEligible(analysisResult)) setEconomyDialogOpen(true);
  }, [analysisResult]);

  const processUpload = useCallback(async (file) => {
    setUploading(true);
    setUploadProgress(10);
    setError('');
    setAnalysisResult(null);
    setAnalysisToken(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadProgress(30);
      const progressInterval = setInterval(() => { setUploadProgress(prev => Math.min(prev + 5, 85)); }, 1500);
      const data = await httpRequest(`${API}/documents/upload-public`, { method: 'POST', body: formData });
      clearInterval(progressInterval);
      setUploadProgress(100);
      if (data.analysis && !data.analysis.error) {
        setAnalysisResult(data.analysis);
        setAnalysisToken(data.analysis_token);
        if (data.analysis_token) localStorage.setItem('pending_analysis_token', data.analysis_token);
        toast.success('Verification terminee.');
      } else if (data.analysis?.error) {
        setError(data.analysis.error);
      } else {
        setError("Impossible d'extraire le texte du document. Verifiez la qualite du scan.");
      }
    } catch (err) {
      setError(uploadErrorMessage(err));
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1500);
    }
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) { toast.error('Format non supporte. Utilisez PDF, JPG ou PNG.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 10MB)'); return; }
    setPendingFile(file);
    setPrivacyDialogOpen(true);
  }, []);

  const handlePrivacyConfirm = useCallback(() => {
    setPrivacyDialogOpen(false);
    if (pendingFile) { processUpload(pendingFile); setPendingFile(null); }
  }, [pendingFile, processUpload]);

  const handlePrivacyCancel = useCallback(() => { setPrivacyDialogOpen(false); setPendingFile(null); }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    maxFiles: 1,
    disabled: uploading,
  });

  const formatCurrency = (val) => {
    if (val == null) return '0 EUR';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const hasSavings = analysisResult && !analysisResult.error && economyCongratsEligible(analysisResult);

  const handleProceedToAuth = () => {
    if (analysisToken) localStorage.setItem('pending_analysis_token', analysisToken);
    setPlanAuthDialogOpen(true);
  };

  const navigateToRegisterAfterPlan = () => {
    navigate('/register');
  };

  const scrollToUpload = () => {
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-black pb-20 md:pb-0" data-testid="landing-page">
      <Header />

      {/* ─── Section 1 — HERO Négociation (proposition principale) ─── */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-20 bg-black relative overflow-hidden" data-testid="hero-negociation">
        {/* halo vert décoratif */}
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(34,197,94,0.18),transparent_55%)]" />
        <div className="relative max-w-3xl mx-auto px-4 md:px-12 flex flex-col items-center text-center">
          <div className="animate-fade-up w-full flex flex-col items-center">
            <p className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-green-400 mb-5">
              <Sparkles size={12} /> Négociation avant signature
            </p>
            <h1 className="text-[26px] sm:text-5xl md:text-6xl tracking-tighter leading-[1.05] font-black text-[#f5f5f7] mb-5 sm:mb-6">
              Avant de signer votre leasing,<br className="hidden sm:block" />
              <span className="text-green-400">je négocie pour vous.</span>
            </h1>
            <p className="text-[15px] md:text-lg leading-relaxed text-[#f5f5f7]/65 mb-7 sm:mb-9 max-w-xl mx-auto">
              Vous avez un devis LLD ou LOA chez un concessionnaire&nbsp;? Envoyez-le moi.
              Je reviens sous <strong className="text-[#f5f5f7]">24&nbsp;h</strong> avec une meilleure offre&nbsp;—
              et les conseils sur le bon kilométrage, la durée et l'assurance.
            </p>

            <a
              href="/negociation"
              data-testid="landing-hero-negociation-cta"
              className="inline-flex items-center gap-3 btn-green px-8 py-4 text-base font-bold mb-4"
            >
              <Handshake size={18} />
              Envoyer mon devis
              <ArrowRight size={18} />
            </a>
            <p className="text-xs text-[#f5f5f7]/40">
              Sans engagement. Commission 10&nbsp;% uniquement sur l'économie obtenue (max 250&nbsp;€).
            </p>

            {/* 3 mini étapes inline */}
            <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-10 w-full max-w-2xl">
              {[
                { n: '1', txt: 'Vous récupérez le devis chez le concessionnaire' },
                { n: '2', txt: 'Vous me l\'envoyez en 30 secondes' },
                { n: '3', txt: 'Je reviens sous 24 h avec mieux' },
              ].map(({ n, txt }) => (
                <div key={n} className="flex flex-col items-center text-center">
                  <span className="w-8 h-8 rounded-full bg-green-500/15 text-green-400 font-bold flex items-center justify-center text-sm mb-2">{n}</span>
                  <p className="text-[11px] sm:text-sm text-[#f5f5f7]/55 leading-snug">{txt}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Bandeau séparateur « ou » entre proposition principale et secondaire ─── */}
      <div className="relative my-8 md:my-14" aria-hidden>
        <div className="max-w-3xl mx-auto px-4 md:px-12">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#f5f5f7]/30">ou</span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/10" />
          </div>
        </div>
      </div>

      {/* ─── Section 2 — SCAN contrat existant (proposition secondaire, compacte) ─── */}
      <section className="pb-14 md:pb-20 bg-black" data-testid="scan-section">
        <div className="max-w-2xl mx-auto px-4 md:px-12">
          <div className="text-center mb-5">
            <p className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-[#f5f5f7]/40 mb-2">
              <ScanLine size={12} /> Déjà sous contrat
            </p>
            <h2 className="text-lg sm:text-xl md:text-2xl tracking-tight leading-tight font-bold text-[#f5f5f7]/85">
              Faites analyser votre contrat existant.
            </h2>
          </div>

          {/* Upload zone — layout horizontal compact */}
          <div ref={uploadSectionRef} className="w-full max-w-xl mx-auto">
            {!analysisResult ? (
              <div className="border border-white/[0.06] rounded-xl overflow-hidden" data-testid="upload-section">
                <div
                  {...getRootProps()}
                  className={`upload-zone flex items-center gap-4 p-4 sm:p-5 text-left cursor-pointer ${isDragActive ? 'drag-active' : ''} ${uploading ? 'opacity-60 cursor-wait' : ''}`}
                  data-testid="upload-dropzone"
                >
                  <input {...getInputProps()} data-testid="upload-input" />
                  <div className="w-10 h-10 bg-[#2c2c2e] flex items-center justify-center rounded-lg shrink-0">
                    <Upload size={18} className="text-[#f5f5f7]/45" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {isDragActive ? (
                      <p className="text-sm font-medium text-green-400">Déposez ici…</p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-[#f5f5f7]/80 leading-tight">Glissez votre contrat ou cliquez ici</p>
                        <p className="text-[11px] text-[#f5f5f7]/40 mt-0.5">PDF · JPG · PNG · 10 Mo max</p>
                      </>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-[#f5f5f7]/30 shrink-0 hidden sm:block" />
                </div>
                {uploading && (
                  <div className="px-4 sm:px-5 pb-4 pt-1" data-testid="upload-progress">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-[#f5f5f7]/60">
                        {uploadProgress < 30 ? 'Envoi...' : uploadProgress < 85 ? 'Vérification du document...' : 'Finalisation...'}
                      </p>
                      <span className="text-xs text-[#f5f5f7]/40">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-1.5 rounded-lg progress-pulse" />
                  </div>
                )}
                {error && (
                  <div className="mx-4 mb-4 flex items-start gap-2 bg-red-950/40 border border-red-500/30 p-3 rounded-lg" data-testid="upload-error">
                    <AlertCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-400">{error}</p>
                  </div>
                )}
              </div>
            ) : null}
            <p className="mt-3 text-center text-[11px] text-[#f5f5f7]/35">
              Gratuit. Pas d'économie = pas un euro à payer.
            </p>
          </div>
        </div>
      </section>

      {SEPARATOR}

      {/* ─── Section 2 — Qui est derriere ? (hidden mobile) ─── */}
      <section className="hidden md:block py-14 md:py-20 bg-black" data-testid="about-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-14 h-14 bg-green-600 flex items-center justify-center rounded-2xl shrink-0 text-2xl font-black text-white font-heading">
              F
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#f5f5f7] mb-1">Fayçal</h2>
              <p className="text-sm text-[#f5f5f7]/40 mb-4">CEO de WeDreamTeam · Fondateur de LeasingMalin</p>
              <p className="text-sm md:text-base leading-relaxed text-[#f5f5f7]/60">
                Passionné de voitures et de tech depuis toujours. 10&nbsp;ans à négocier des LOA et LLD pour moi et mon entourage. Des dizaines de contacts dans le milieu&nbsp;— vendeurs, responsables de marques. J'ai appris ce qui se négocie vraiment, et comment. LeasingMalin, c'est cette méthode mise à votre service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {SEPARATOR}

      {/* ─── Section 3 — Comment ca marche (hidden mobile) ─── */}
      <section className="hidden md:block py-14 md:py-20 bg-black" data-testid="how-it-works">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-green-400 mb-4">Comment ça marche</p>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#f5f5f7] mb-10">Trois étapes. C'est tout.</h2>
          <div className="space-y-8">
            {[
              {
                num: '1',
                icon: ScanLine,
                title: 'Scannez votre contrat',
                desc: 'Gratuit, sans engagement. Vos données sont chiffrées et supprimées après analyse.',
              },
              {
                num: '2',
                icon: Search,
                title: 'On détecte l\'économie possible',
                desc: 'Si aucune économie n\'est détectée, vous vous arrêtez là. Zéro frais.',
              },
              {
                num: '3',
                icon: Handshake,
                title: 'Vous choisissez votre formule',
                desc: 'Seulement si une économie existe. Vous payez après avoir vu le résultat.',
              },
            ].map(({ num, icon: Icon, title, desc }) => (
              <div key={num} className="flex items-start gap-5">
                <div className="w-10 h-10 bg-green-500/15 border border-green-500/20 flex items-center justify-center rounded-xl shrink-0">
                  <Icon size={18} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#f5f5f7] mb-1">{title}</h3>
                  <p className="text-sm leading-relaxed text-[#f5f5f7]/50">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {SEPARATOR_ALWAYS}

      {/* ─── Section 4 — Les formules ─── */}
      <section className="py-10 md:py-20 bg-black" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-4 md:px-12">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-green-400 mb-3 md:mb-4">Les formules</p>
          <h2 className="text-xl sm:text-3xl tracking-tight font-bold text-[#f5f5f7] mb-6 md:mb-10">
            Uniquement si une économie est détectée
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-12">
            {/* Essentiel */}
            <div className="bg-[#1c1c1e] border border-white/[0.08] rounded-2xl p-5 md:p-7 flex flex-col">
              <h3 className="text-xl font-black font-heading text-[#f5f5f7] mb-1">Essentiel</h3>
              <p className="text-sm text-[#f5f5f7]/40 mb-5">Vous négociez, on vous guide.</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-[#f5f5f7] font-heading">20&nbsp;€</span>
                <span className="text-sm text-[#f5f5f7]/40 ml-1.5">+ 5&nbsp;% de l'économie annuelle</span>
              </div>
              <p className="text-sm leading-relaxed text-[#f5f5f7]/50 flex-1">
                Vous recevez un email avec la méthode et les instructions pas à pas. Vous négociez vous-même.
              </p>
              <div className="mt-6 bg-black/40 border border-white/[0.04] rounded-xl px-4 py-3">
                <p className="text-xs text-[#f5f5f7]/35">
                  <Shield size={11} className="inline mr-1 text-green-400" />
                  Aucun frais si pas d'économie détectée
                </p>
              </div>
            </div>

            {/* Serenite */}
            <div className="relative bg-[#1c1c1e] border-2 border-blue-500/40 rounded-2xl p-5 md:p-7 flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-blue-600 text-white border-0 rounded-full px-4 py-1 text-xs font-semibold">On s'occupe de tout</Badge>
              </div>
              <h3 className="text-xl font-black font-heading text-[#f5f5f7] mb-1">Sérénité</h3>
              <p className="text-sm text-[#f5f5f7]/40 mb-5">Fayçal négocie pour vous.</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-[#f5f5f7] font-heading">20&nbsp;€</span>
                <span className="text-sm text-[#f5f5f7]/40 ml-1.5">+ 15&nbsp;% de l'économie annuelle</span>
              </div>
              <p className="text-sm leading-relaxed text-[#f5f5f7]/50 flex-1">
                Fayçal prend en charge toutes les démarches via mandat signé. Si ça n'aboutit pas, le pourcentage est remboursé intégralement.
              </p>
              <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3">
                <p className="text-xs text-blue-400">
                  <Shield size={11} className="inline mr-1" />
                  Garanti : si ça n'aboutit pas, seuls les 20&nbsp;€ restent dus.
                </p>
              </div>
            </div>
          </div>

          {/* Tableau comparatif */}
          <div className="bg-[#1c1c1e] border border-white/[0.08] rounded-2xl overflow-hidden">
            <div className="px-4 md:px-6 py-5 border-b border-white/[0.06]">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Exemple concret</p>
              <p className="text-sm text-[#f5f5f7]/50">
                Votre contrat est surévalué de <span className="font-bold text-[#f5f5f7]">50&nbsp;€/mois</span>, soit <span className="font-bold text-green-400">600&nbsp;€/an</span> d'économie détectée.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-4 md:px-6 py-3 text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30"></th>
                    <th className="text-center px-4 md:px-6 py-3 text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/50">Essentiel</th>
                    <th className="text-center px-4 md:px-6 py-3 text-xs uppercase tracking-[0.1em] font-semibold text-blue-400">Serenite</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Frais fixes', essential: '20 €', serenite: '20 €' },
                    { label: 'Commission', essential: '5 % × 600 € = 30 €', serenite: '15 % × 600 € = 90 €' },
                    { label: 'Total payé', essential: '50 €', serenite: '110 €', bold: true },
                    { label: 'Vous économisez', essential: '550 €/an', serenite: '490 €/an', green: true, bold: true },
                    { label: 'Vous négociez', essential: 'Vous-même', serenite: 'Fayçal s\'en charge' },
                    { label: 'Garantie remboursement', essential: '—', serenite: 'Oui (hors 20 €)', blue: true },
                  ].map(({ label, essential, serenite, bold, green, blue }, i) => (
                    <tr key={i} className="border-b border-white/[0.04] last:border-0">
                      <td className="px-4 md:px-6 py-3 text-[#f5f5f7]/50">{label}</td>
                      <td className={`px-4 md:px-6 py-3 text-center ${bold ? 'font-black font-heading' : ''} ${green ? 'text-green-400' : 'text-[#f5f5f7]/70'}`}>{essential}</td>
                      <td className={`px-4 md:px-6 py-3 text-center ${bold ? 'font-black font-heading' : ''} ${green ? 'text-green-400' : blue ? 'text-blue-400' : 'text-[#f5f5f7]/70'}`}>{serenite}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {SEPARATOR}

      {/* ─── Section 5 — Meilleur prix ? (hidden mobile) ─── */}
      <section className="hidden md:block py-14 md:py-20 bg-black" data-testid="best-price-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#f5f5f7] mb-4">
            Et si mon contrat est déjà au meilleur prix&nbsp;?
          </h2>
          <p className="text-sm md:text-base leading-relaxed text-[#f5f5f7]/50 max-w-xl mx-auto">
            Ça arrive — environ 20&nbsp;% des contrats analysés sont déjà bien négociés dès le départ.
            Dans ce cas, on vous le dit clairement, et vous ne payez rien. C'est aussi ça la promesse LeasingMalin&nbsp;: un avis honnête, pas une économie inventée.
          </p>
        </div>
      </section>

      {SEPARATOR}

      {/* ─── Section 6 — Donnees protegees (hidden mobile) ─── */}
      <section className="hidden md:block py-14 md:py-20 bg-black" data-testid="security-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-green-400 mb-4">Vos données</p>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#f5f5f7] mb-8">Protégées à chaque étape</h2>
          <div className="space-y-4">
            {[
              'Votre contrat est chiffré dès l\'envoi et supprimé après analyse.',
              'Vos données ne sont jamais revendues ni partagées.',
              'Traitement conforme au RGPD.',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-green-400 mt-0.5 shrink-0" />
                <p className="text-sm md:text-base text-[#f5f5f7]/60">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {SEPARATOR}

      {/* ─── Section 8 — CTA final (desktop) ─── */}
      <section className="hidden md:block py-16 md:py-24 bg-black" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold text-[#f5f5f7] mb-4">
            Prêt à savoir si vous payez trop cher&nbsp;?
          </h2>
          <p className="text-sm md:text-base text-[#f5f5f7]/50 mb-8">
            Scan gratuit. Résultat en quelques minutes. Aucun engagement.
          </p>
          <button
            onClick={scrollToUpload}
            className="btn-green inline-flex items-center gap-3 px-8 py-4 text-base font-semibold"
            data-testid="cta-scan-btn"
          >
            Scanner mon contrat gratuitement
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ─── Mobile sticky CTA ─── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-lg border-t border-white/[0.08] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <p className="text-[11px] text-[#f5f5f7]/40 text-center mb-2">Aucun engagement. Résultat en quelques minutes.</p>
        <button
          onClick={scrollToUpload}
          className="w-full btn-green flex items-center justify-center gap-2 px-6 py-3.5 text-[15px] font-semibold"
          data-testid="cta-mobile-btn"
        >
          Scanner mon contrat gratuitement
          <ArrowRight size={16} />
        </button>
      </div>

      {/* ─── Engagement CO2 ─── */}
      <section className="py-8 bg-black">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-2 text-xs text-[#f5f5f7]/40">
            <Leaf size={12} className="text-green-400" />
            1&nbsp;% de chaque achat reversé pour la réduction du CO₂
          </div>
        </div>
      </section>

      {/* ─── ANALYSIS RESULTS ─── */}
      {analysisResult && !analysisResult.error && (
        <section ref={resultsSectionRef} className="pb-16 md:pb-24 scroll-mt-24" data-testid="analysis-results-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12 animate-fade-up">
            <PostScanSummary result={analysisResult} formatCurrency={formatCurrency} />
            {analysisToken && (analysisResult.markdown_report || '').trim() ? (
              <p className="text-center text-xs text-[#f5f5f7]/50 mt-4 mb-2">
                <a href={`${API}/analyses/public/${analysisToken}/report.md`} className="underline hover:text-[#f5f5f7]/90" download data-testid="download-synthese-md">
                  Télécharger la synthèse complète (.md)
                </a>
              </p>
            ) : null}
            {hasSavings ? (
              <div className="bg-green-500/10 border border-green-500/30 p-8 mt-6 rounded-xl" data-testid="savings-found-card">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 size={24} className="text-green-400" />
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-green-300 font-heading">Économie possible</h2>
                </div>
                <p className="text-sm text-green-300/90 mb-6">
                  Une <strong>réduction de mensualité</strong> peut être envisagée sur la base de ce document.
                  Comparez les formules Essentiel et Sérénité, puis créez un compte pour lancer la prise en charge.
                </p>
                {user ? (
                  <button onClick={() => navigate('/dashboard')} data-testid="go-dashboard-btn" className="w-full btn-green flex items-center justify-center gap-3 px-8 py-4 text-base">
                    Choisir ma formule
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button onClick={handleProceedToAuth} data-testid="proceed-auth-btn" className="w-full btn-green flex items-center justify-center gap-3 px-8 py-4 text-base">
                    Choisir ma formule et m&apos;inscrire
                    <ArrowRight size={18} />
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-black border border-white/[0.08] p-8 mt-6 text-center rounded-xl" data-testid="no-savings-card">
                <AlertCircle size={32} className="mx-auto text-[#f5f5f7]/40 mb-4" />
                <h2 className="text-2xl font-bold mb-2 font-heading text-[#f5f5f7]">Votre contrat semble au juste prix</h2>
                <p className="text-base text-[#f5f5f7]/50 mb-6 max-w-md mx-auto">
                  Aucune marge d'économie n'a été identifiée. Vous ne payez rien. Vous pouvez réessayer avec un autre document.
                </p>
                <button onClick={() => { setAnalysisResult(null); setAnalysisToken(null); setError(''); }} data-testid="try-again-btn" className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm">
                  Analyser un autre document
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Dialogs ─── */}
      <EconomyPossibleDialog open={economyDialogOpen} onOpenChange={setEconomyDialogOpen} result={analysisResult} formatCurrency={formatCurrency} />

      <PlanChoiceBeforeAuthDialog
        open={planAuthDialogOpen}
        onOpenChange={setPlanAuthDialogOpen}
        analysisResult={analysisResult}
        formatCurrency={formatCurrency}
        onContinueToRegister={navigateToRegisterAfterPlan}
      />

      <Dialog open={privacyDialogOpen} onOpenChange={setPrivacyDialogOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] rounded-2xl sm:rounded-2xl bg-[#1c1c1e] border-white/[0.08] p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader className="text-left space-y-3">
              <div className="w-12 h-12 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center rounded-2xl">
                <EyeOff size={22} className="text-blue-400" />
              </div>
              <DialogTitle className="text-[#f5f5f7] font-heading text-lg">Protégez vos informations personnelles</DialogTitle>
              <DialogDescription className="text-[#f5f5f7]/50 text-sm leading-relaxed">
                Avant d'envoyer votre document, vous pouvez masquer les informations non nécessaires à l'analyse :
              </DialogDescription>
            </DialogHeader>
            <ul className="mt-4 space-y-2">
              {['Nom et prénom', 'Numéro de téléphone', 'Nom de la société', 'Adresse personnelle'].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-[#f5f5f7]/60">
                  <EyeOff size={12} className="text-blue-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-[#f5f5f7]/40 leading-relaxed">
              Ces informations ne seront demandées que si une économie est avérée et que vous souhaitez poursuivre.
            </p>
          </div>
          <DialogFooter className="p-6 pt-5 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handlePrivacyCancel} className="w-full sm:w-auto rounded-xl border-white/[0.08] bg-transparent text-[#f5f5f7]/60 hover:text-[#f5f5f7] hover:bg-[#2c2c2e]">
              Annuler
            </Button>
            <Button onClick={handlePrivacyConfirm} className="w-full sm:w-auto rounded-xl bg-green-600 text-white hover:bg-green-500" data-testid="privacy-confirm-btn">
              J'ai compris, envoyer le document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Footer ─── */}
      <footer className="border-t border-white/[0.06] bg-black py-8" data-testid="footer">
        <div className="max-w-3xl mx-auto px-6 md:px-12 flex flex-col items-center justify-center gap-3 text-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-600 flex items-center justify-center rounded">
              <span className="text-white font-black text-xs font-heading">LM</span>
            </div>
            <span className="text-sm font-semibold font-heading text-[#f5f5f7]">LeasingMalin.com</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#f5f5f7]/30">
            <a href="/cgv" className="hover:text-[#f5f5f7]/60 transition-opacity duration-[180ms]">CGV</a>
            <span>·</span>
            <a href="/confidentialite" className="hover:text-[#f5f5f7]/60 transition-opacity duration-[180ms]">Confidentialité</a>
          </div>
          <p className="text-xs text-[#f5f5f7]/30">2026 LeasingMalin.com — Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
